import { useEffect, useCallback } from "react";
import { LayoutGrid, Save, Trash2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLocalStorage } from "@/hooks/useLocalStorage";
import {
  applyWorkspaceTemplate,
  deleteCustomTemplate,
  DEFAULT_TEMPLATES,
  loadCustomTemplates,
  WORKSPACE_TEMPLATES_STORAGE_KEY,
} from "@/lib/templates";
import { cn } from "@/lib/utils";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import type { WorkspaceTemplate } from "@/types/workspace";

export function TemplateSelector() {
  const activeTemplateId = useWorkspaceStore((state) => state.activeTemplateId);
  const [customTemplates, setCustomTemplates] = useLocalStorage<WorkspaceTemplate[]>(
    WORKSPACE_TEMPLATES_STORAGE_KEY,
    [],
  );
  const templates = [...DEFAULT_TEMPLATES, ...customTemplates];

  useEffect(() => {
    const syncTemplates = () => {
      setCustomTemplates(loadCustomTemplates());
    };

    window.addEventListener("lytick:templates-updated", syncTemplates);
    return () => window.removeEventListener("lytick:templates-updated", syncTemplates);
  }, [setCustomTemplates]);

  const saveCurrentTemplate = useCallback(() => {
    window.dispatchEvent(new CustomEvent("lytick:save-chart-template"));
  }, []);

  const removeTemplate = useCallback((templateId: string) => {
    deleteCustomTemplate(templateId);
    setCustomTemplates((currentTemplates) =>
      currentTemplates.filter((template) => template.id !== templateId),
    );
  }, [setCustomTemplates]);

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Workspace templates"
              className="relative inline-flex items-center justify-center w-9 h-9 rounded-[var(--radius-md)] text-[color:var(--lyt-text-muted)] transition-colors duration-[var(--duration-fast)] hover:bg-[var(--surface-tint-hover)] hover:text-[color:var(--lyt-text-secondary)] border-0 bg-transparent cursor-pointer focus-visible:outline-none"
            >
              <LayoutGrid className="h-3.5 w-3.5" strokeWidth={1.5} />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom" sideOffset={6}>
          Templates
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent
        align="end"
        className="w-80 border p-2 shadow-xl"
        style={{
          backgroundColor: "var(--bg-elevated)",
          borderColor: "var(--border-default)",
          color: "var(--text-primary)",
        }}
      >
        <DropdownMenuLabel style={{ color: "var(--text-secondary)" }}>
          Workspace Templates
        </DropdownMenuLabel>
        {templates.map((template) => {
          const isActive = template.id === activeTemplateId;

          return (
            <DropdownMenuItem
              key={template.id}
              className={cn(
                "interactive interactive-list flex items-start gap-3 rounded-md border px-3 py-2",
                isActive && "interactive-selected interactive-selected-border",
              )}
              style={{
                borderColor: isActive ? "var(--accent-primary)" : "transparent",
                backgroundColor: isActive
                  ? "color-mix(in srgb, var(--accent-primary) 15%, transparent)"
                  : "transparent",
              }}
              onSelect={(event) => {
                event.preventDefault();
                applyWorkspaceTemplate(template);
              }}
            >
              <span
                className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full border"
                style={{
                  borderColor: "var(--border-default)",
                  backgroundColor: "var(--bg-overlay)",
                  color: "var(--text-primary)",
                  fontSize: "var(--text-xs)",
                  fontWeight: 700,
                }}
              >
                {template.icon}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span style={{ color: "var(--text-primary)", fontSize: "var(--text-sm)" }}>
                    {template.name}
                  </span>
                  {!template.isDefault ? (
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        removeTemplate(template.id);
                      }}
                      className="interactive interactive-icon inline-flex h-7 w-7 items-center justify-center rounded-md"
                      style={{ color: "var(--text-secondary)" }}
                      aria-label={`Delete ${template.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
                <p
                  className="mt-1 leading-4"
                  style={{ color: "var(--text-secondary)", fontSize: "var(--text-xs)" }}
                >
                  {template.description}
                </p>
              </div>
            </DropdownMenuItem>
          );
        })}
        <DropdownMenuSeparator />
        {customTemplates.length === 0 && (
          <p
            className="px-3 py-1"
            style={{ color: "var(--text-muted)", fontSize: "var(--text-xs)" }}
          >
            Save your layout as a custom template below.
          </p>
        )}
        <DropdownMenuItem
          className="interactive interactive-list rounded-md px-3 py-2"
          onSelect={(event) => {
            event.preventDefault();
            saveCurrentTemplate();
          }}
        >
          <Save className="h-4 w-4" />
          <span>Save Current</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
